import { useFormStatus } from 'react-dom';
import { BUTTONS, LABELS, MESAJE, TITLES, PAGE_METADATA, FORM_MESSAGES, AUTH_UI } from "@buzunarul/shared-constants";
import React from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router";
import { useAuthStore } from "../../../stores/authStore";

// Document metadata
import { useDocumentMeta } from "../../../hooks/useDocumentMeta";

// CVA styling imports
import { button, card, cn, spaceY } from "../../../styles/cva-v2";

// Form utilities
import { createFormAction } from "../../../utils/formActions";
import { useFormAction } from "../../../hooks/useFormAction";

import Alert from "../../primitives/Alert/Alert";
import Input from "../../primitives/Input/Input";
import { GoogleButton } from "../../primitives/GoogleButton";
import { BuzunarLogo } from "../../shared/BuzunarLogo";

/**
 * Submit button component using useFormStatus
 */
function SubmitButton({ isFormValid }: { isFormValid: boolean }) {
  const { pending } = useFormStatus();
  
  return (
    <button
      type="submit"
      disabled={!isFormValid || pending}
      className={cn(
        button({ variant: "primary", size: "md" }),
        "w-full",
        !isFormValid && "opacity-50 cursor-not-allowed"
      )}
      data-testid="login-submit"
    >
      {pending ? FORM_MESSAGES.SUBMITTING : BUTTONS.LOGIN}
    </button>
  );
}

/**
 * Login form action
 */
const loginAction = createFormAction<{ email: string; password: string }>(
  async (data) => {
    const { login } = useAuthStore.getState();
    
    // Call the login function from auth store
    await login(data.email, data.password);
    
    // Check if login was successful
    const { error, errorType } = useAuthStore.getState();
    
    if (error) {
      // Map error types to user-friendly messages
      let errorMessage = error;
      switch (errorType) {
        case "INVALID_CREDENTIALS":
          errorMessage = MESAJE.EROARE_AUTENTIFICARE || "Date de autentificare incorecte.";
          break;
        case "RLS_DENIED":
          errorMessage = MESAJE.EROARE_RLS || "Acces interzis (RLS).";
          break;
        case "NETWORK":
          errorMessage = MESAJE.EROARE_RETEA || "Eroare de rețea. Încearcă din nou.";
          break;
        default:
          errorMessage = error || MESAJE.LOGIN_ERROR;
      }
      
      return { 
        error: errorMessage,
        success: false 
      };
    }
    
    return { 
      success: true,
      data: { email: data.email, password: data.password }
    };
  }
);

const LoginForm = () => {
  // Document metadata
  useDocumentMeta({
    title: PAGE_METADATA.AUTH.LOGIN.title,
    description: PAGE_METADATA.AUTH.LOGIN.description,
  });

  const navigate = useNavigate();
  const { loginWithGoogle, loading: googleLoading } = useAuthStore();
  
  // Use the form action hook
  const { formAction, isPending, error } = useFormAction(
    loginAction,
    {},
    {
      onSuccess: () => {
        toast.success(MESAJE.LOGIN_SUCCES);
        navigate("/transactions");
      },
      onError: (error) => {
        toast.error(error);
      }
    }
  );

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  // Form validation state
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const isFormValid = isValidEmail(email) && password.length >= 6;

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      // Google OAuth will handle the redirect
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : MESAJE.GOOGLE_AUTH_ERROR;
      toast.error(errorMessage);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* Authentication Card */}
      <form
        className={cn(
          card({ variant: "elevated" }),
          "w-full",
        )}
        action={formAction}
        data-testid="login-form"
      >
        {/* Logo + Welcome */}
        <div className="flex flex-col items-center py-6 space-y-4 border-b border-gray-200 dark:border-gray-700">
          <BuzunarLogo className="w-20 h-20" variant="icon" />
          <h1 className="text-2xl font-bold text-center text-gray-800 dark:text-gray-100">
            {TITLES.LOGIN_WELCOME}
          </h1>
        </div>

        <div className={cn("p-lg", spaceY({ spacing: 4 }))}>
          {/* Email Input */}
          <Input
            id="email"
            name="email"
            type="email"
            label={`${LABELS.EMAIL}*`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            variant="default"
            data-testid="login-email"
          />

          {/* Password Input */}
          <Input
            id="password"
            name="password"
            type="password"
            label={`${LABELS.PAROLA}*`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            variant="default"
            data-testid="login-password"
          />

          {/* Error Message */}
          {error && (
            <Alert
              variant="error"
              data-testid="login-error"
            >
              {error}
            </Alert>
          )}

          {/* Submit Button with useFormStatus */}
          <SubmitButton isFormValid={isFormValid} />

          {/* Divider */}
          <div className="relative">
            <div className="flex absolute inset-0 items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="flex relative justify-center text-sm">
              <span className="px-2 text-gray-500 bg-white">{AUTH_UI.DIVIDER_OR}</span>
            </div>
          </div>

          {/* Google Login Button */}
          <GoogleButton
            onClick={handleGoogleLogin}
            isLoading={googleLoading || isPending}
            variant="login"
            data-testid="google-login-button"
          />
          
          {/* Google Auth Explanation */}
          <p className="text-xs text-center text-gray-500">
            {AUTH_UI.GOOGLE_AUTO_CREATE}
          </p>

          {/* Register Link */}
          <div className="text-center">
            <Link
              to="/register"
              className={cn(
                button({ variant: "ghost", size: "sm" }),
                "text-primary-600 hover:text-primary-700",
              )}
              data-testid="switch-to-register"
            >
              {AUTH_UI.SWITCH_TO_REGISTER}
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;